/// <reference types="vite/client" />

import moment from 'moment'

// ─── Data Fetching ─────────────────────────────────────────────────────────

/**
 * Fetch JSON from a URL with basic error handling.
 * For GeoJSON layers, prefer using L.geoJSON() with a fetch call here.
 */
export async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`)
  // Extend ImportMeta to include 'env' for Vite
  }
  return res.json() as Promise<T>
}

// ─── Date Formatting ──────────────────────────────────────────────────────

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return 'N/A'
  return moment(date).format('MMM D, YYYY')
}

export function formatDateRelative(date: string | Date | null | undefined): string {
  if (!date) return 'N/A'
  return moment(date).fromNow()
}

// ─── Number Formatting ────────────────────────────────────────────────────

export function formatDepth(feet: number | null | undefined): string {
  if (feet == null) return 'N/A'
  return `${feet.toLocaleString()} ft`
}

// ─── Environment Helpers ──────────────────────────────────────────────────

/**
 * Access a required VITE_ environment variable.
 * Throws at runtime if the variable is missing so misconfiguration
 * is caught early rather than silently producing undefined values.
 */
export function requireEnv(key: string): string {
  const value = (import.meta.env as Record<string, string | undefined>)[key]
  if (!value) {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
      `Add it to your .env file as ${key}=<value>`
    )
  }
  return value
}
